import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FaCheckCircle, FaMapMarkerAlt, FaLink, FaFlask, FaMap,
  FaRegCalendarAlt, FaUserPlus, FaEnvelope, FaSearch, FaBell,
  FaCommentAlt, FaFilePdf, FaExternalLinkAlt, FaQuoteRight, FaUsers, FaEdit
} from 'react-icons/fa';
import './Profile.css';

// ── Helpers ─────────────────────────────────────────────────────────────────
const Avatar = ({ src, size = 160 }) => (
  <div className="rn-avatar-container" style={{ width: size, height: size }}>
    <div className="rn-avatar-glow"></div>
    <div className="rn-avatar-inner">
      <img src={src || '/researcher_avatar.png'} alt="Profile" />
    </div>
  </div>
);

// ── Publication Card ────────────────────────────────────────────────────────
const PublicationCard = ({ pub }) => (
  <div className="rn-pub-card">
    <div className="rn-pub-header">
      <span className="rn-pub-journal">{pub.journal}</span>
      <span className="rn-pub-date">{pub.date}</span>
    </div>
    <h3 className="rn-pub-title">{pub.title}</h3>
    <p className="rn-pub-snippet">{pub.snippet}</p>
    <div className="rn-pub-links">
      <span className="rn-pub-doi">DOI: {pub.doi}</span>
      <button className="rn-pub-pdf-btn"><FaFilePdf /> Texto completo PDF</button>
    </div>
  </div>
);

// ── Mock Data ────────────────────────────────────────────────────────────────
const MOCK_DATA = {
  name: 'Dra. Elena Rodríguez',
  badge: 'EXPERTO VERIFICADO',
  role: 'Líder Senior de Investigación Clínica en los Institutos Nacionales de Salud (NIH)',
  location: 'Bethesda, MD',
  tags: ['Oncología y Biología Molecular'],
  link: 'nih.gov/rodriguez-lab',
  stats: [
    { label: 'ESTUDIOS ACTIVOS', value: '5', sub: '2 Ensayos Fase III', icon: '🧪' },
    { label: 'COLABORADORES', value: '184', sub: 'En 14 países' },
  ],
  publications: [
    {
      journal: 'NATURE MEDICINE',
      date: 'Oct 2023',
      title: 'Mecanismos moleculares de la inmunoterapia basada en ARNm en el melanoma en estadio avanzado: Una revisión clínica',
      snippet: 'Esta investigación destaca nuevas vías a través de las cuales las vacunas de ARNm activan la respuesta de las células T en pacientes previamente resistentes...',
      doi: '10.1030/s41591-023-0123'
    },
    {
      journal: 'THE LANCET ONCOLOGY',
      date: 'May 2023',
      title: 'Disparidad global en el acceso a ensayos clínicos oncológicos innovadores: Un análisis de 10 años',
      snippet: 'Un análisis exhaustivo de las ubicaciones de los ensayos entre 2013 y 2023 que muestra una concentración significativa en naciones de altos ingresos...',
      doi: '10.1016/S1470-2045(23)'
    },
    {
      journal: 'CELL REPORTS',
      date: 'Ene 2023',
      title: 'Marcadores genéticos de resistencia terapéutica en pacientes pediátricos con neuroblastoma',
      snippet: 'Identificación de polimorfismos de nucleótido único clave que se correlacionan con la recurrencia temprana en cohortes de oncología pediátrica...',
      doi: '10.1016/j.celrep.2022'
    }
  ],
  interests: ['Inmunoterapia', 'Genómica', 'Ensayos Clínicos', 'Biomarcadores', 'Medicina de Precisión'],
  events: [
    { day: '12', month: 'NOV', title: 'Cumbre Global de Salud', role: 'PONENTE PRINCIPAL' },
    { day: '05', month: 'DIC', title: 'Reunión Anual ASCO', role: 'PANELISTA' }
  ]
};

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Publicaciones');

  const avatarPath = user?.profile?.photoUrl
    ? (user.profile.photoUrl.startsWith('http') ? user.profile.photoUrl : `http://localhost:5000${user.profile.photoUrl}`)
    : '/researcher_avatar.png';

  // Map user data to a format compatible with the UI
  const profileData = {
    name: user?.profile ? `${user.profile.firstName} ${user.profile.lastName}` : 'Cargando...',
    badge: user?.status === 'VALIDATED' ? 'EXPERTO VERIFICADO' : 'PERFIL EN REVISIÓN',
    role: user?.professionalProfile?.profession || 'Investigador',
    location: user?.profile?.country || 'Ubicación no especificada',
    tags: user?.professionalProfile?.researchAreas || [],
    link: user?.professionalProfile?.website || '',
    stats: [
      { label: 'ESTUDIOS ACTIVOS', value: user?.professionalProfile?.activeStudies || '0', icon: '🧪' },
      { label: 'COLABORADORES', value: user?.professionalProfile?.collaboratorsCount || '0' },
    ],
    publications: user?.profile?.articles || [],
    interests: user?.professionalProfile?.specialties || [],
    projects: user?.profile?.projects || []
  };

  return (
    <div className="rn-profile-page">
      {/* ── HEADER ── */}
      <header className="rn-header">
        <div className="rn-header-inner">
          <div className="rn-logo" onClick={() => navigate('/', { state: { from: 'internal' } })}>
            <div className="rn-logo-box">R</div>
            <span>ResearchNet</span>
          </div>
          <div className="rn-search-container">
            <FaSearch className="rn-search-icon" />
            <input type="text" placeholder="Buscar investigadores, artículos, centros..." />
          </div>
          <nav className="rn-nav-links">
            <button>Descubrir</button>
            <button className="active">Red</button>
            <button onClick={() => navigate('/researcher-library', { state: { from: 'internal' } })}>Revistas</button>
            <button onClick={() => navigate('/scientific-events', { state: { from: 'internal' } })}>Eventos</button>
          </nav>
          <div className="rn-header-actions">
            <button className="rn-icon-btn"><FaBell /><span className="rn-dot" /></button>
            <button className="rn-icon-btn"><FaCommentAlt /></button>
            <div className="rn-mini-avatar" onClick={() => navigate('/profile', { state: { from: 'internal' } })}>
              <img src={avatarPath} alt="User" />
            </div>
          </div>
        </div>
      </header>

      <main className="rn-container">
        {/* ── PROFILE TOP SECTION ── */}
        <section className="rn-profile-top">
          <Avatar src={avatarPath} />
          <div className="rn-profile-info">
            <div className="rn-name-row">
              <h1>{profileData.name}</h1>
              <span className="rn-badge"><FaCheckCircle /> {profileData.badge}</span>
              <button className="rn-btn-edit-inline" onClick={() => navigate('/profile/edit', { state: { from: 'internal' } })}>
                <FaEdit /> Editar Perfil
              </button>
            </div>
            <p className="rn-role">{profileData.role}</p>
            <div className="rn-meta-row">
              <span><FaFlask /> {profileData.tags[0] || 'Sin líneas de investigación'}</span>
              <span><FaMapMarkerAlt /> {profileData.location}</span>
              {profileData.link && <span><FaLink /> {profileData.link}</span>}
            </div>
          </div>
          <div className="rn-profile-actions">
            <button className="rn-btn-follow">Seguir</button>
            <button className="rn-btn-contact">Contactar</button>
          </div>
        </section>

        {/* ── STATS ROW ── */}
        <section className="rn-stats-grid">
          {profileData.stats.map((stat, i) => (
            <div key={i} className="rn-stat-card">
              <p className="rn-stat-label">{stat.label}</p>
              <p className="rn-stat-value">{stat.value}</p>
              <p className="rn-stat-sub">
                {stat.trend === 'up' && <span className="rn-trend-up">↗</span>}
                {stat.sub || 'Métrica actualizada'}
              </p>
              {stat.icon && <span className="rn-stat-icon">{stat.icon}</span>}
            </div>
          ))}
        </section>

        {/* ── CONTENT AREA ── */}
        <div className="rn-content-layout">
          {/* LEFT CONTENT: TABS & LISTS */}
          <div className="rn-left-content">
            <nav className="rn-tabs">
              {['Publicaciones', 'Proyectos Activos', 'Red de Colaboración', 'Eventos'].map(tab => (
                <button
                  key={tab}
                  className={activeTab === tab ? 'active' : ''}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </nav>

            <div className="rn-tab-content">
              {activeTab === 'Publicaciones' && (
                profileData.publications.length > 0 ? (
                  profileData.publications.map((pub, i) => (
                    <PublicationCard key={i} pub={{
                      journal: pub.journal,
                      date: pub.publicationDate,
                      title: pub.title,
                      snippet: pub.description,
                      doi: pub.link || 'N/A'
                    }} />
                  ))
                ) : (
                  <p className="rn-empty-state">No hay publicaciones registradas.</p>
                )
              )}
              {activeTab === 'Proyectos Activos' && (
                profileData.projects.length > 0 ? (
                  profileData.projects.map((proj, i) => (
                    <div key={i} className="rn-pub-card">
                      <div className="rn-pub-header">
                        <span className="rn-pub-journal">{proj.startDate} - {proj.endDate}</span>
                      </div>
                      <h3 className="rn-pub-title">{proj.title}</h3>
                      <p className="rn-pub-snippet">{proj.description}</p>
                      {proj.details && <p className="rn-pub-details"><strong>Rol:</strong> {proj.details}</p>}
                    </div>
                  ))
                ) : (
                  <p className="rn-empty-state">No hay proyectos activos registrados.</p>
                )
              )}
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <aside className="rn-sidebar">
            {/* Interests */}
            <div className="rn-sidebar-card">
              <h3><FaFlask /> Áreas de Interés</h3>
              <div className="rn-interest-tags">
                {profileData.interests.length > 0 ? (
                  profileData.interests.map(tag => (
                    <span key={tag} className="rn-tag">{tag}</span>
                  ))
                ) : (
                  <p className="rn-empty-small">Sin especialidades registradas.</p>
                )}
              </div>
            </div>

            {/* Network Map */}
            <div className="rn-sidebar-card">
              <h3><FaMap /> Mapa de Contactos</h3>
              <div className="rn-network-viz">
                <div className="rn-viz-dots">
                  <div className="dot d1"></div>
                  <div className="dot d2"></div>
                  <div className="dot d3"></div>
                  <svg className="rn-viz-line">
                    <line x1="20%" y1="60%" x2="50%" y2="30%" stroke="#FF6600" strokeWidth="1" strokeDasharray="3,3" />
                    <line x1="50%" y1="30%" x2="80%" y2="70%" stroke="#FF6600" strokeWidth="1" strokeDasharray="3,3" />
                  </svg>
                </div>
                <div className="rn-viz-footer">
                  <p>Alcance Global</p>
                  <span>42 Nodos Institucionales</span>
                </div>
                <button className="rn-viz-btn">Expandir Visualizador de Red</button>
              </div>
            </div>

            {/* Events */}
            <div className="rn-sidebar-card">
              <h3><FaRegCalendarAlt /> Próximos Eventos</h3>
              <div className="rn-event-list">
                {MOCK_DATA.events.map((ev, i) => (
                  <div key={i} className="rn-event-item">
                    <div className="rn-ev-date">
                      <span className="rn-ev-month">{ev.month}</span>
                      <span className="rn-ev-day">{ev.day}</span>
                    </div>
                    <div className="rn-ev-info">
                      <p className="rn-ev-title">{ev.title}</p>
                      <p className="rn-ev-role">{ev.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* ── FOOTER ── */}
      <footer className="rn-footer">
        <div className="rn-footer-inner">
          <div className="rn-footer-left">
            <div className="rn-logo-small">R</div>
            <span>ResearchNet</span>
          </div>
          <div className="rn-footer-links">
            <button>Política de Privacidad</button>
            <button>Términos de Servicio</button>
            <button>Acceso Institucional</button>
          </div>
          <p className="rn-copyright">© 2024 ResearchNet. Impulsando el descubrimiento clínico global.</p>
        </div>
      </footer>
    </div>
  );
};

export default Profile;